import { useMemo } from 'react';
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Button } from '../../../components/ui/Button';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import { fetcher } from '../../../features/schemes/network/schemes.api';
import { Plus } from 'lucide-react';

interface SchemeListPageItem {
  id: string;
  schemeNumber: string;
  name: string | null;
  chitValuePaise: number;
  _count?: { subscribers: number };
  totalTickets: number;
  status: string;
  startDate: string | null;
}

const schemesConfig = defineListPage<SchemeListPageItem>({
  variant: 'data-table',
  resource: 'schemes',
  columns: [
    {
      id: 'schemeNumber',
      header: 'Scheme Number',
      accessor: 'schemeNumber',
    },
    { id: 'name', header: 'Name', accessor: (row) => row.name ?? '-', sortable: true },
    {
      id: 'chitValuePaise',
      header: 'Chit Value',
      accessor: 'chitValuePaise',
      renderType: 'money',
      numeric: true,
    },
    {
      id: 'subscribers',
      header: 'Subscribers',
      accessor: (row) => `${row._count?.subscribers ?? 0} / ${row.totalTickets}`,
      numeric: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      renderType: 'status',
      renderMeta: {
        colorMap: {
          ACTIVE: 'success',
          DRAFT: 'warning',
          PENDING_REGISTRATION: 'warning',
          REGISTERED: 'info',
          READY: 'info',
          SUSPENDED: 'danger',
          CLOSING: 'warning',
          CLOSED: 'info',
          COMPLETED: 'success',
          TERMINATED: 'danger',
          REJECTED: 'danger',
        },
      },
    },
    {
      id: 'startDate',
      header: 'Commencement Date',
      accessor: 'startDate',
      renderType: 'date',
    },
  ],
  defaultPageSize: 20,
  onRowClick: (row) => window.open(`/schemes/${row.id}`, '_self'),
  rowActions: [
    {
      label: 'View Scheme',
      onClick: (row) => window.open(`/schemes/${row.id}`, '_self'),
    },
    {
      label: 'Manage Cycles',
      onClick: (row) => window.open(`/schemes/${row.id}?tab=Cycles`, '_self'),
    },
    {
      label: 'View Subscribers',
      onClick: (row) => window.open(`/schemes/${row.id}?tab=Subscribers`, '_self'),
    },
    {
      label: 'Scheme Ledger',
      onClick: (row) => window.open(`/schemes/${row.id}?tab=Ledger`, '_self'),
    },
  ],
});

const fetchSchemes: PaginatedQueryFn<SchemeListPageItem> = async (params) => {
  const qs = new URLSearchParams({ page: params.page || '1', limit: params.limit || '20' }).toString();
  return fetcher.get<{ data: SchemeListPageItem[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/chit-groups?${qs}`,
  );
};

export const Route = createFileRoute('/_auth/schemes/')({
  component: SchemeList,
});

function SchemeList() {
  usePageTitle('Chit Schemes');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const urlAdapter = useMemo(
    () => createUrlAdapter(search, navigate as any),
    [search, navigate],
  );

  const list = useListPage(schemesConfig, fetchSchemes, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Chit Schemes"
        subtitle="Manage chit fund schemes and monitor their lifecycle."
        action={
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/templates">
                Scheme Templates
              </Link>
            </Button>
            <Button asChild variant="primary">
              <Link to="/schemes/new">
                <Plus className="h-4 w-4 mr-1.5" />
                Create Scheme
              </Link>
            </Button>
          </div>
        }
      />

      <DataTable
        config={schemesConfig}
        list={list}
      />
    </div>
  );
}
